
import React, { useState, useEffect } from 'react';
import {
    AddContainer, Component4, Texts, InputTextComponent,
    PrescriptionViewBox, PresText, PresTitle, EditButton, SaveButton, LabelWithTooltip
} from './Style';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Button, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import useResponsive from 'src/components/hooks/useResponsive';
import { getTemplateByClinicIdRequest } from 'src/modules/app/store/appActions';
import { ReactComponent as Edit } from '../../../../../assets/images/edit-2.svg';
import { ReactComponent as Close } from '../../../../../assets/images/close-circle.svg';
import { ReactComponent as Add } from '../../../../../assets/images/add-square.svg';
import theme from 'src/config/theme';
const PrescriptionTemplate = ({ innerRef, data, setClinicProfileData, clinicId }) => {
    const { isMobile, isTablet } = useResponsive();
    const Width = isMobile || isTablet ? '18px' : '20px';
    const Height = isMobile || isTablet ? '18px' : '20px';

    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    
    // Helper to format raw templates synchronously on initial mount
    const formatPrescriptionTemplates = (rawTemplates) => {
        if (!Array.isArray(rawTemplates)) return [];
        return rawTemplates
            .filter(template => template !== null && typeof template === 'object')
            .map((template, idx) => ({
                id: template.id || template._id || idx,
                label: template.title || template.label || "Untitled",
                value: template.title || template.label || "Untitled",
                text: template.text || "",
            }));
    };

    const [localTemplates, setLocalTemplates] = useState(() => formatPrescriptionTemplates(data));
    const dispatch = useDispatch();

    useEffect(() => {
        setLocalTemplates(formatPrescriptionTemplates(data));
    }, [JSON.stringify(data)]);


    const handleEdit = (template) => {
        setSelectedTemplate(template);
        setOpenDialog(true);
    };

    const handleDelete = (templateToDelete) => {
        const updatedTemplates = localTemplates.filter(t => t.id !== templateToDelete.id);

        setLocalTemplates(updatedTemplates);

        setClinicProfileData(prevData => ({
            ...prevData,
            prescriptionTemplate: updatedTemplates.map(t => ({
                title: t.label,
                text: t.text,
                type: 'prescription',
            })),
        }));
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
        setSelectedTemplate(null);
    };

    const handleSubmit = (values) => {
        if (!values.title.trim() || !values.text.trim()) return;

        setLocalTemplates(prev => {
            const isExisting = selectedTemplate
                ? prev.find(t => t.id === selectedTemplate.id)
                : false;

            let updatedTemplates;

            if (isExisting) {
                updatedTemplates = prev.map(t =>
                    t.id === selectedTemplate.id ? {
                        ...t,
                        label: values.title,
                        value: values.title,
                        text: values.text,
                    } : t
                );
            } else {
                const duplicate = prev.find(t =>
                    t.label.trim() === values.title.trim() &&
                    t.text.trim() === values.text.trim()
                );
                if (duplicate) return prev;

                updatedTemplates = [
                    ...prev,
                    {
                        id: Math.random().toString(36).substr(2, 9),
                        label: values.title,
                        value: values.title,
                        text: values.text,
                    }
                ];
            }

            const uniqueTemplates = updatedTemplates.filter((t, index, self) =>
                index === self.findIndex(
                    s => s.label.trim() === t.label.trim() && s.text.trim() === t.text.trim()
                )
            );

            setClinicProfileData(prevData => ({
                ...prevData,
                prescriptionTemplate: uniqueTemplates.map(t => ({
                    title: t.label,
                    text: t.text,
                    type: 'prescription',
                })),
            }));

            return uniqueTemplates;
        });

        handleDialogClose();
    };

    return (
        <>
            <AddContainer>
                {localTemplates.map((template) => (
                    <PrescriptionViewBox
                        key={template.id}
                    >
                        <Box>
                            <PresTitle>{template.label}</PresTitle>
                            <PresText>{template.text}</PresText>
                        </Box>
                        <Box sx={{ display: 'flex', gap: '8px' }}>
                            <EditButton type="button" onClick={() => handleEdit(template)}>
                                <Edit width={Width} />
                            </EditButton>
                            <EditButton type="button" onClick={() => handleDelete(template)}>
                                <Close width={Width} />
                            </EditButton>
                        </Box>
                    </PrescriptionViewBox>
                ))}

                <Box display="flex" justifyContent="flex-end" mt={2}>



                    <SaveButton
                        variant="outlined"
                        startIcon={<Add width={Width} height={Height} sx={{ color: theme.palette.primary.main }} />}
                        onClick={() => {
                            setSelectedTemplate(null);
                            setOpenDialog(true);
                        }}
                    >
                        Add
                    </SaveButton>
                </Box>
            </AddContainer>

            {openDialog && (
                <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="md" >
                    <DialogTitle>
                        {selectedTemplate ? 'Edit Prescription Template' : 'Add New Prescription Template'}
                    </DialogTitle>
                    <Formik
                        initialValues={{
                            type: 'prescription',
                            title: selectedTemplate?.label || '',
                            text: selectedTemplate?.text || '',
                        }}
                        enableReinitialize
                        onSubmit={handleSubmit}
                        innerRef={innerRef}
                    >
                        {({ values, handleChange, handleBlur }) => (
                            <Form>
                                <DialogContent>
                                    <Component4>
                                        <Texts>
                                            <LabelWithTooltip
                                                label="Prescription Template"
                                                tooltip="Enter a name for this template (e.g., Fever Rx, Diabetes Initial)."
                                            />
                                        </Texts>
                                        <InputTextComponent
                                            name="title"
                                            variant="outlined"
                                            value={values.title}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </Component4>

                                    <Component4>
                                        <Texts>
                                            <LabelWithTooltip
                                                label="Prescription"
                                                tooltip="Write the prescription text here. This will be inserted into the patient's record."
                                            />
                                        </Texts>
                                        <InputTextComponent
                                            name="text"
                                            value={values.text}
                                            variant="outlined"
                                            placeholder="Enter"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            fullWidth
                                            multiline
                                            rows={4}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '12px',
                                                    height: '120px',
                                                    alignItems: 'flex-start',
                                                },
                                            }}
                                        />
                                    </Component4>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={handleDialogClose} variant="outlined">Cancel</Button>
                                    <Button type="submit" variant="contained" color="primary">
                                        Save
                                    </Button>
                                </DialogActions>
                            </Form>
                        )}
                    </Formik>
                </Dialog>
            )}
        </>
    );
};

export default React.memo(PrescriptionTemplate);
